/**
 * rolesApi — typed client for the real M02 IAM Role Master + User-Role
 * assignment backend (`@Controller('v1/roles')`, reached as `/api/roles`).
 *
 * This module currently covers role listing and the user↔role assignment
 * lifecycle used by the User Master screens. Role CRUD (create/clone/
 * deactivate) is extended onto this module when the Role Master screens are
 * rewired to the real contract.
 */
import { apiRequest } from './client.js';

/** T015 role_master row. */
export interface Role {
  readonly roleId: string;
  readonly roleCode: string;
  readonly roleName: string;
  readonly roleCategoryCode: string;
  readonly roleDescription: string | null;
  readonly isSystemRole: boolean;
  readonly recordStatusCode: string;
  readonly version: number;
}

/**
 * The licence bucket a role's holders are counted under. Licences are sold per
 * category, so a role must declare one.
 */
export const ROLE_CATEGORIES = [
  'TENANT_ADMIN',
  'TENANT_STAFF',
  'VENDOR',
  'WORKFORCE',
  'AUDITOR',
  'PLATFORM_ADMIN',
] as const;
export type RoleCategory = (typeof ROLE_CATEGORIES)[number];

/** §8 role lifecycle statuses. */
export const ROLE_STATUSES = ['ACTIVE', 'INACTIVE', 'ARCHIVED'] as const;
export type RoleStatus = (typeof ROLE_STATUSES)[number];

export const ROLE_STATUS_LABELS: Readonly<Record<string, string>> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  ARCHIVED: 'Archived',
};

export interface CreateRoleInput {
  readonly roleCode: string;
  readonly roleName: string;
  readonly roleCategoryCode: string;
  readonly roleDescription?: string;
  readonly effectiveFromDate: string;
}

export interface UpdateRoleInput {
  readonly roleName?: string;
  readonly roleDescription?: string;
  readonly version: number;
}

/** T020 user_role_map row (a single role assignment for a user). */
export interface UserRoleMap {
  readonly mapId: string;
  readonly userId: string;
  readonly roleId: string;
  readonly assignedBy?: string;
  readonly assignedAt?: string;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly recordStatusCode: string;
  readonly reviewStatus: string;
}

type Tok = string | null | undefined;

/** GET /v1/roles — all active roles for the tenant. */
export function listRoles(token?: Tok): Promise<readonly Role[]> {
  return apiRequest<readonly Role[]>('/roles', { token });
}

/**
 * GET /v1/roles?status=… — roles filtered by lifecycle status. Pass 'ALL' to
 * retrieve every role regardless of status (Role Master management view).
 */
export function listRolesByStatus(status: string, token?: Tok): Promise<readonly Role[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiRequest<readonly Role[]>(`/roles${qs}`, { token });
}

/** GET /v1/roles/:roleId — a single role. */
export function getRole(roleId: string, token?: Tok): Promise<Role> {
  return apiRequest<Role>(`/roles/${roleId}`, { token });
}

/** POST /v1/roles — create a role. */
export function createRole(body: CreateRoleInput, token?: Tok): Promise<Role> {
  return apiRequest<Role>('/roles', { method: 'POST', token, body });
}

/** PATCH /v1/roles/:roleId — update name/description (optimistic lock via version). */
export function updateRole(roleId: string, body: UpdateRoleInput, token?: Tok): Promise<Role> {
  return apiRequest<Role>(`/roles/${roleId}`, { method: 'PATCH', token, body });
}

/** POST /v1/roles/:roleId/clone — clone a role (copies its permissions). */
export function cloneRole(
  roleId: string,
  newRoleCode: string,
  newRoleName: string,
  token?: Tok,
): Promise<Role> {
  return apiRequest<Role>(`/roles/${roleId}/clone`, {
    method: 'POST', token, body: { newRoleCode, newRoleName },
  });
}

/** What a copy-rights run actually changed. */
export interface CopyRightsResult {
  readonly permissionsAdded: number;
  readonly menusAdded: number;
}

/**
 * POST /v1/roles/:roleId/copy-rights-from/:sourceRoleId — give one role the
 * rights another already has. `replace` clears the target's own rights first.
 */
export function copyRoleRights(
  roleId: string,
  sourceRoleId: string,
  replace: boolean,
  token?: Tok,
): Promise<CopyRightsResult> {
  return apiRequest<CopyRightsResult>(
    `/roles/${roleId}/copy-rights-from/${sourceRoleId}?replace=${replace}`,
    { method: 'POST', token },
  );
}

/** POST /v1/roles/:roleId/deactivate — deactivate a role (§8 lifecycle). */
export function deactivateRole(roleId: string, token?: Tok): Promise<Role> {
  return apiRequest<Role>(`/roles/${roleId}/deactivate`, { method: 'POST', token });
}

/** POST /v1/roles/:roleId/archive — archive a role (§8 lifecycle). */
export function archiveRole(roleId: string, token?: Tok): Promise<Role> {
  return apiRequest<Role>(`/roles/${roleId}/archive`, { method: 'POST', token });
}

/** GET /v1/roles/:userId/user-roles — the user's active role assignments. */
export function getUserRoles(userId: string, token?: Tok): Promise<readonly UserRoleMap[]> {
  return apiRequest<readonly UserRoleMap[]>(`/roles/${userId}/user-roles`, { token });
}

/**
 * GET /v1/roles/me — the roles the signed-in user holds, names included.
 * Self-service: no userId, no ROLES_VIEW permission, because a user may always
 * see which roles they are in.
 */
export function getMyRoles(token?: Tok): Promise<readonly Role[]> {
  return apiRequest<readonly Role[]>('/roles/me', { token });
}

/** POST /v1/roles/assign-user — assign a role to a user. */
export function assignUserRole(userId: string, roleId: string, token?: Tok): Promise<UserRoleMap> {
  return apiRequest<UserRoleMap>('/roles/assign-user', {
    method: 'POST', token, body: { userId, roleId },
  });
}

/** DELETE /v1/roles/user-role/:mapId — revoke a role assignment. */
export function revokeUserRole(mapId: string, token?: Tok): Promise<void> {
  return apiRequest<void>(`/roles/user-role/${mapId}`, { method: 'DELETE', token });
}

// ── Permission catalog + role-permission mapping (Permission Master, §9/§11) ──

/** T016 permission_master row (an action the system can authorize). */
export interface Permission {
  readonly permissionId: string;
  readonly permissionCode: string;
  readonly permissionName: string;
  readonly permissionCategory: string;
  readonly description?: string | null;
  readonly isSystemPermission?: boolean;
}

/** T017 role_permission_map row (one action granted to a role on a resource). */
export interface RolePermissionMap {
  readonly mapId: string;
  readonly roleId: string;
  readonly permissionId: string;
  readonly resourceType: string;
  readonly recordStatusCode: string;
}

/** One row per ACTIVE role; `permissions` is empty for a role with no grants. */
export interface RolePermissionGrants {
  readonly roleId: string;
  readonly permissions: readonly RolePermissionMap[];
}

/** GET /v1/permissions — the tenant's permission catalog. */
export function listPermissions(token?: Tok): Promise<readonly Permission[]> {
  return apiRequest<readonly Permission[]>('/permissions', { token });
}

/** GET /v1/roles/:roleId/permissions — a role's active permission grants. */
export function getRolePermissions(roleId: string, token?: Tok): Promise<readonly RolePermissionMap[]> {
  return apiRequest<readonly RolePermissionMap[]>(`/roles/${roleId}/permissions`, { token });
}

/**
 * GET /v1/roles/permission-matrix — every role's grants in one request.
 * The matrix screen used to call getRolePermissions once per role, which
 * tripped the API rate limit once a tenant had more than a handful of roles.
 */
export function getPermissionMatrix(token?: Tok): Promise<readonly RolePermissionGrants[]> {
  return apiRequest<readonly RolePermissionGrants[]>('/roles/permission-matrix', { token });
}

/** POST /v1/roles/:roleId/permissions — grant an action on a resource to a role. */
export function assignPermission(
  roleId: string,
  permissionId: string,
  resourceType: string,
  token?: Tok,
): Promise<RolePermissionMap> {
  return apiRequest<RolePermissionMap>(`/roles/${roleId}/permissions`, {
    method: 'POST', token, body: { permissionId, resourceType },
  });
}

/** DELETE /v1/roles/:roleId/permissions/:mapId — revoke an action grant. */
export function revokePermission(roleId: string, mapId: string, token?: Tok): Promise<void> {
  return apiRequest<void>(`/roles/${roleId}/permissions/${mapId}`, { method: 'DELETE', token });
}

/**
 * What every tenant made from now on is born holding (t017c).
 *
 * Not the same thing as the grants above. Those say what a role may do inside
 * THIS tenant; these say what a role starts with in every tenant created after
 * today, which the provisioning seed reads. Editing them changes the future and
 * never an existing tenant.
 */
export interface RoleBirthRight {
  readonly permissionCode: string;
  readonly permissionName: string | null;
  readonly resourceType: string;
}

export interface RoleBirthRights {
  readonly roleCode: string;
  readonly roleName: string | null;
  readonly permissions: readonly RoleBirthRight[];
}

export interface BirthRightChoices {
  readonly roles: ReadonlyArray<{ roleCode: string; roleName: string | null }>;
  readonly permissions: ReadonlyArray<{
    permissionCode: string;
    permissionName: string | null;
    permissionCategory: string | null;
  }>;
}

/** GET /v1/roles/born-with — the whole template, role by role. */
export function listBirthRights(token?: Tok): Promise<readonly RoleBirthRights[]> {
  return apiRequest<readonly RoleBirthRights[]>('/roles/born-with', { token });
}

/** GET /v1/roles/born-with/choices — the roles and rights it may speak about. */
export function listBirthRightChoices(token?: Tok): Promise<BirthRightChoices> {
  return apiRequest<BirthRightChoices>('/roles/born-with/choices', { token });
}

/** One tenant, measured against the template. */
export interface BirthRightDrift {
  readonly tenantCode: string;
  readonly tenantId: string;
  /** The template offers it, this tenant does not hold it — it is on its way. */
  readonly missing: ReadonlyArray<{ roleCode: string; permissionCode: string }>;
  /** This tenant granted it itself. Reported, never touched. */
  readonly beyond: ReadonlyArray<{ roleCode: string; permissionCode: string }>;
}

/** GET /v1/roles/born-with/drift — where each tenant stands against the template. */
export function listBirthRightDrift(token?: Tok): Promise<readonly BirthRightDrift[]> {
  return apiRequest<readonly BirthRightDrift[]>('/roles/born-with/drift', { token });
}

/** POST /v1/roles/born-with — platform administrator only. */
export function grantBirthRight(
  roleCode: string,
  permissionCode: string,
  resourceType: string,
  token?: Tok,
): Promise<unknown> {
  return apiRequest<unknown>('/roles/born-with', {
    method: 'POST', token, body: { roleCode, permissionCode, resourceType },
  });
}

/** DELETE /v1/roles/born-with/:roleCode/:permissionCode — platform administrator only. */
export function revokeBirthRight(
  roleCode: string,
  permissionCode: string,
  token?: Tok,
): Promise<void> {
  return apiRequest<void>(
    `/roles/born-with/${encodeURIComponent(roleCode)}/${encodeURIComponent(permissionCode)}`,
    { method: 'DELETE', token },
  );
}

